/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Endpoint } from '../index';
import { z, ZodTypes } from '../zod';

const Params = z.strictObject({
  organizationId: ZodTypes.Id,
  projectId: ZodTypes.Id,
  clusterId: ZodTypes.Id,
});

export const PatchCluster = {
  id: 'cluster.patch',
  tag: 'Cluster',
  summary: 'Modify cluster configuration',
  description: 'Modify a cluster configuration within an organization project',
  method: 'patch',
  flags: { isAuthorized: true },
  route: (params: z.infer<typeof Params>) =>
    `/organization/${params.organizationId}/project/${params.projectId}/cluster/${params.clusterId}`,
  request: {
    params: Params,
    body: {
      required: true,
      content: {
        'application/json': {
          schema: z.strictObject({
            metadata: z
              .strictObject({
                name: z.string().optional(),
                description: z.string().optional().nullable(),
                projectId: z.string().optional(),
              })
              .optional(),
            configuration: z
              .strictObject({
                replicas: z.number().int().min(0).optional(),
                supportPlanId: ZodTypes.Id.optional(),
                cpuUnits: z.number().min(0.25).optional(),
                memoryGbs: z.number().int().min(1).optional(),
                storageGbs: z.number().int().min(1).optional(),
                allowedSourceRanges: z.array(ZodTypes.IpAndRange).nullable().optional(),
                backup: z
                  .strictObject({
                    logical: z
                      .strictObject({
                        enabled: z.boolean().optional(),
                        schedule: z
                          .string()
                          .regex(/^(\d+|\*)(\/\d+)?(\s+(\d+|\*)(\/\d+)?){4}$/)
                          .optional(),
                        daysToRetain: z.number().positive().optional(),
                      })
                      .optional(),
                    continuous: z
                      .strictObject({
                        enabled: z.boolean().optional(),
                        schedule: z
                          .string()
                          .regex(/^(\d+|\*)(\/\d+)?(\s+(\d+|\*)(\/\d+)?){4}$/)
                          .optional(),
                        daysToRetain: z.number().positive().optional(),
                      })
                      .optional(),
                  })
                  .optional(),
                maintenanceWindows: z.array(z.string()).optional(),
                postgresqlConfig: z.object({}).passthrough().optional(),
                pgHba: z
                  .array(
                    z.strictObject({
                      type: ZodTypes.PgHba.ConnectionTypes,
                      database: z.array(z.string().min(1)).or(z.string().min(1)),
                      user: z.array(z.string().min(1)).or(z.string().min(1)),
                      address: z.string().min(1),
                      authenticationMethod: ZodTypes.PgHba.AuthenticationMethod,
                    }),
                  )
                  .optional(),
              })
              .optional(),
          }),
        },
      },
    },
  },
  response: {
    200: {
      description: 'successful',
      content: {
        'application/json': {
          schema: z.string().optional(),
        },
      },
    },
  },
} as const satisfies Endpoint;
